import {
  Badge,
  Button,
  Card,
  Descriptions,
  Image as AntImage,
  Layout,
  Modal,
  Space,
  Switch,
  Tag,
  Tooltip,
  message,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getFormatDateWithTimeStamp,
  storageConstants,
} from "../../../common/utils";
import { Image } from "../../../common/utils/image";
import EditRequestVerification from "./EditRequestVerification";
import { getRequestVerificationDetailsById } from "./RequestVerification.service";
import { cancelRequest } from "../../../services/api";
import { setUserHideFromDiscovery } from "../UserManagement.service";
import UserTooltip from "../../common/user-tooltip/userTooltip";
const { Content } = Layout;
const { Paragraph } = Typography;

const ViewRequestVerification = (props) => {
  const navigate = useNavigate();
  const params = useParams();
  const [verificationData, setVerificationData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [documentType, setDocumentType] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [discoveryUpdating, setDiscoveryUpdating] = useState(false);

  useEffect(() => {
    getRequestVerificationDetails(params.id);
    return () => {
      cancelRequest();
    };
  }, []);

  const getRequestVerificationDetails = (id) => {
    getRequestVerificationDetailsById(id)
      .then((data) => {
        if (data && data.success) {
          setVerificationData(data.data);
        } else {
          message.error(data.message ? data.message : "failed");
        }
      })
      .catch((error) => {
        message.error(error.message ? error.message : "failed");
      });
  };

  const getStatus = (status) => {
    switch (status) {
      case "Viewed":
        return "processing";
        break;
      case "Approved":
        return "success";
        break;
      case "Rejected":
        return "error";
        break;
      default:
        return "warning";
        break;
    }
  };

  const getFileExtension = (url) => {
    const lastDotIndex = url.lastIndexOf(".");
    if (lastDotIndex !== -1) {
      setDocumentType(
        url.substring(lastDotIndex + 1) === "pdf" ? "pdf" : "image"
      );
      setShowModal(true);
    }
    return null;
  };

  const editHandler = (value, isUpdated = false) => {
    setIsEdit(value);
    if (isUpdated) {
      getRequestVerificationDetails(params.id);
    }
  };

  const handleHideFromDiscoveryChange = (hideFromDiscovery) => {
    const userId = verificationData?.user?.userId;
    if (!userId) {
      message.error("User not found");
      return;
    }
    setDiscoveryUpdating(true);
    setUserHideFromDiscovery(userId, hideFromDiscovery)
      .then((data) => {
        if (data && data.success) {
          message.success(
            hideFromDiscovery
              ? "User hidden from discovery"
              : "User visible in discovery"
          );
          setVerificationData((prev) => ({
            ...prev,
            user: { ...prev.user, hideFromDiscovery },
          }));
        } else {
          message.error(data?.message || "Failed to update discovery setting");
        }
      })
      .catch((error) => {
        message.error(error.message || "Failed to update discovery setting");
      })
      .finally(() => {
        setDiscoveryUpdating(false);
      });
  };

  return (
    <Content>
      <Card
      // title="Request Verification Details"
      >
        <Descriptions
          bordered
          title="Request Verification Details"
          size={"small"}
          extra={
            <Button type="primary" onClick={() => setIsEdit(true)}>
              Edit
            </Button>
          }
        >
          {verificationData && (
            <>
              <Descriptions.Item label="Full Name">
                {verificationData.fullName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Badge
                  status={
                    verificationData.status
                      ? getStatus(verificationData.status)
                      : "default"
                  }
                  text={verificationData.status || "-"}
                />
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {verificationData?.category?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Face Images">
                {verificationData.faceImages && verificationData.faceImages.length > 0 ? (
                  <AntImage.PreviewGroup>
                    <Space wrap size={[8, 8]}>
                      {verificationData.faceImages.map((imgUrl, index) => (
                        <AntImage
                          key={index}
                          src={imgUrl}
                          alt={`Face image ${index + 1}`}
                          width={64}
                          height={64}
                          style={{
                            objectFit: "cover",
                            borderRadius: "6px",
                            border: "1px solid #d9d9d9",
                          }}
                        />
                      ))}
                    </Space>
                  </AntImage.PreviewGroup>
                ) : (
                  "-"
                )}
              </Descriptions.Item>
              {verificationData.documentUrl && (
                <>
                  <Descriptions.Item label="Document Type">
                    {verificationData.documentType?.name || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Document">
                    <Button
                      type="primary"
                      onClick={() => getFileExtension(verificationData.documentUrl)}
                      disabled={!verificationData.documentUrl}
                    >
                      View
                    </Button>
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label="Nationality">
                {(verificationData.nationality &&
                  verificationData.nationality.name) ||
                  "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Audience">
                {verificationData.audience || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Industry Type">
                {verificationData?.industryType?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Submitted At">
                {(verificationData.submittedAt &&
                  getFormatDateWithTimeStamp(verificationData.submittedAt)) ||
                  "-"}
              </Descriptions.Item>

              <Descriptions.Item label="Approved At">
                {(verificationData.approvedAt &&
                  getFormatDateWithTimeStamp(verificationData.approvedAt)) ||
                  "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Approved By">
                {verificationData.approvedBy || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Rejected At">
                {(verificationData.rejectedAt &&
                  getFormatDateWithTimeStamp(verificationData.rejectedAt)) ||
                  "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Rejected By">
                {verificationData.rejectedBy || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Comments">
                <Paragraph
                  ellipsis={{ rows: 1, expandable: true, symbol: "more" }}
                >
                  {verificationData.comments || "-"}
                </Paragraph>
                {/* {verificationData.comments || "-"} */}
              </Descriptions.Item>
              {verificationData.links &&
                verificationData.links.map((item, index) => {
                  return (
                    <Descriptions.Item
                      key={index}
                      label={"Link Type " + item.type}
                    >
                      {item.url || "-"}
                    </Descriptions.Item>
                  );
                })}
            </>
          )}
        </Descriptions>
        <div style={{ marginTop: "20px" }}>
          <Descriptions bordered title="User Details" size={"small"}>
            {verificationData && verificationData.user && (
              <>
                <Descriptions.Item label="Full Name">
                  {verificationData.user?.fullName || "-"}
                </Descriptions.Item>

                <Descriptions.Item label="User Name">
                  <a
                    onClick={() => {
                      navigate(
                        `${storageConstants.PAGES.ROUTE.USER_MANAGEMENT}/user/${verificationData?.user?.userId}`
                      );
                    }}
                  >
                    <UserTooltip
                      userName={verificationData?.user?.userName || "-"}
                      userId={verificationData?.user?.userId}
                      verified={verificationData?.user?.verified}
                    />
                  </a>
                </Descriptions.Item>
                <Descriptions.Item label="Account Type">
                  <Tag
                    color={
                      verificationData?.user?.isPrivate ? "#2db7f5" : "#87d068"
                    }
                  >
                    {verificationData?.user?.isPrivate ? "Private" : "Public"}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Hide From Discovery">
                  <Tooltip
                    title={
                      verificationData?.user?.hideFromDiscovery
                        ? "Hidden from map and discovery feeds"
                        : "Visible in map and discovery feeds"
                    }
                  >
                    <Switch
                      checked={!!verificationData?.user?.hideFromDiscovery}
                      disabled={discoveryUpdating}
                      checkedChildren="True"
                      unCheckedChildren="False"
                      onChange={handleHideFromDiscoveryChange}
                    />
                  </Tooltip>
                </Descriptions.Item>
              </>
            )}
          </Descriptions>
        </div>
        <Modal
          title="Document"
          open={showModal && verificationData.documentUrl}
          onCancel={() => {
            setShowModal(false);
          }}
          footer={null}
        >
          <Image url={verificationData.documentUrl} type={documentType}></Image>
        </Modal>

        {isEdit && (
          <EditRequestVerification
            props={props}
            isShow={isEdit}
            id={verificationData.id}
            name={verificationData.fullName}
            status={verificationData.status}
            isShowHandler={editHandler}
          />
        )}
      </Card>
    </Content>
  );
};
export default ViewRequestVerification;
